import React from 'react';
import { SIDEBAR_ITEMS } from './Sidebar.jsx';

/* v8 Path G — SubViewShell utilities for focus-mode sub-pages.
   Each tab owns a flat list of SectionCard / NestedSection blocks. When
   the sidebar routes to a sub-item, we want ONE of those blocks to take
   over the viewport — not a scroll-and-pray landing on the overview. The
   hooks/components below do exactly that with minimum surgery:

     - isSubViewActive(activeSection, sectionId)  → gate a block's render
     - isOverviewMode(activeSection)              → "show everything"
     - <SubViewBreadcrumb/>                       → "← Back to X overview"
   */

/* List of section IDs that are treated as focus-mode sub-views.
   Built from the Sidebar config so it stays in sync automatically.
   Values that aren't in this list (undefined, 'overview', null) mean
   "show the whole overview". */
const SUB_VIEW_IDS = (() => {
  const set = new Set();
  for (const item of SIDEBAR_ITEMS) {
    if (!Array.isArray(item.children)) continue;
    for (const ch of item.children) {
      if (ch.id && ch.id !== 'overview') set.add(ch.id);
    }
  }
  return set;
})();

/* Section IDs that are "handled natively" by a tab's internal subTab
   state. Currently empty — Proposal 3 (2026-04-25) removed the last
   native subTab usage when Journal Entries moved from Actuals to FCC.
   Kept as the extension point if a future tab needs it. */
const NATIVE_SUBTAB_IDS = new Set([]);

export function isOverviewMode(activeSection) {
  if (!activeSection) return true;
  if (activeSection === 'overview') return true;
  if (NATIVE_SUBTAB_IDS.has(activeSection)) return true;
  if (!SUB_VIEW_IDS.has(activeSection)) return true;  /* unknown → treat as overview */
  return false;
}

export function isSubViewActive(activeSection, sectionId) {
  return !isOverviewMode(activeSection) && activeSection === sectionId;
}

/* Render decision helper:
     - overview mode → render EVERY block (returns true for all)
     - focused mode  → render only when activeSection matches ANY of the
                       provided names
   Call from a tab like `showBlock('recurring', 'family-ap')` — blocks
   that are relevant to multiple sub-pages stay visible for all of them. */
export function makeShowBlock(activeSection) {
  const overview = isOverviewMode(activeSection);
  return (...names) => {
    if (overview) return true;
    return names.includes(activeSection);
  };
}

export function shouldRenderBlock(activeSection, sectionId) {
  if (isOverviewMode(activeSection)) return true;
  return activeSection === sectionId;
}

/* Lookup the human-readable label for a section so the breadcrumb can
   display something other than the raw slug. Fallback: capitalize the id. */
function labelFor(moduleId, sectionId) {
  const mod = SIDEBAR_ITEMS.find(i => i.id === moduleId);
  const child = mod?.children?.find(c => c.id === sectionId);
  return child?.label || sectionId.replace(/-/g, ' ');
}

/* Breadcrumb shown at the top of a tab when a sub-view is active.
   Click → clears activeSection (onBack). Shows module > section path. */
export function SubViewBreadcrumb({ moduleId, activeSection, onBack }) {
  if (isOverviewMode(activeSection)) return null;
  const mod = SIDEBAR_ITEMS.find(i => i.id === moduleId);
  if (!mod) return null;
  const sectionLabel = labelFor(moduleId, activeSection);
  return (
    <nav
      className="flex items-center gap-2 mb-3 px-[0.7rem] py-[0.5rem] bg-surface-el border border-border rounded-sm text-[0.8rem]"
      aria-label="Sub-view breadcrumb"
    >
      <button
        type="button"
        className="bg-transparent border border-border text-text-muted hover:text-text hover:border-accent-gold px-[0.55rem] py-[0.15rem] rounded-sm cursor-pointer text-[0.75rem] font-[inherit] transition-colors"
        onClick={onBack}
        title={`Back to ${mod.label} overview`}
      >
        ← {mod.label} overview
      </button>
      <span className="text-text-dim" aria-hidden>›</span>
      <strong className="text-accent-gold">{sectionLabel}</strong>
    </nav>
  );
}
